// BattleTunePill — long pill button + popover with the same dial design
// as the per-surface Tune module (ModelOutputPanel). Battle-scoped only —
// the silo settings tune is NOT touched. Last-used values are sticky in
// localStorage per "slot" (challenger / opponent / referee / cross) so
// next time the modal opens they land where the user left them.
//
// Variants:
//   - Contestant (default): Model + Max output + Conversation memory + Summary bridge
//   - Referee  (variant="referee"): adds Temperature (Precise → Wild),
//     and renames the bottom two: "Debate Memory" + "Context Retention"
//
// Auto-applies on the parent's submit — there's no Save button. The
// parent reads the current value via the `value` + `onChange` props.

import { useEffect, useRef, useState } from "react";
import { Cpu, BookmarkSimple, ArrowsCounterClockwise, X, CaretDown, Sparkle } from "@phosphor-icons/react";
import InfoTip from "@/components/InfoTip";
import StyleSelector from "@/components/AnswerStyleSelector";
import { api } from "@/lib/api";

const STORAGE_PREFIX = "battle_tune_v1_";

// Provider/model registry for battles. Mirrors what /api/models/registry
// returns; we hard-code the cap values here so the pill works even if the
// network call fails. Updated whenever AVAILABLE_MODELS_REGISTRY changes
// in server.py.
const BATTLE_REGISTRY_FALLBACK = {
  platform_default_model: "gemini-2.5-pro",
  providers: [
    {
      provider: "gemini",
      label: "Gemini (Google)",
      recommended: "gemini-2.5-pro",
      models: ["gemini-2.5-pro", "gemini-2.5-flash"],
      max_output_cap: { "gemini-2.5-pro": 8192, "gemini-2.5-flash": 8192 },
      pricing_per_1m: { "gemini-2.5-pro": { in: 1.25, out: 10 }, "gemini-2.5-flash": { in: 0.075, out: 0.30 } },
    },
    {
      provider: "anthropic",
      label: "Anthropic Claude",
      recommended: "claude-sonnet-4-5-20250929",
      models: ["claude-sonnet-4-5-20250929", "claude-haiku-4-5"],
      max_output_cap: { "claude-sonnet-4-5-20250929": 64000, "claude-haiku-4-5": 64000 },
      pricing_per_1m: { "claude-sonnet-4-5-20250929": { in: 3, out: 15 }, "claude-haiku-4-5": { in: 0.80, out: 4 } },
    },
    {
      provider: "openai",
      label: "OpenAI",
      recommended: "gpt-4o",
      models: ["gpt-4o", "gpt-4o-mini"],
      max_output_cap: { "gpt-4o": 16384, "gpt-4o-mini": 16384 },
      pricing_per_1m: { "gpt-4o": { in: 5, out: 15 }, "gpt-4o-mini": { in: 0.15, out: 0.60 } },
    },
  ],
};

function defaultsFor(variant) {
  // Variant-specific defaults. Both contestant and referee start at gemini-2.5-pro
  // with reasonable values; user can change once and we'll remember.
  const base = {
    provider: "gemini",
    model: "gemini-2.5-pro",
    max_tokens: 4096,
    // Feb 14, 2026 — answer style preset (how silos respond). Contestant-only;
    // Feb 17, 2026 — question style moved to the referee variant.
    answer_style_preset: "concise",
    answer_style_custom: null,
  };
  if (variant === "referee") {
    return {
      ...base,
      model: "claude-sonnet-4-5-20250929",
      provider: "anthropic",
      // Feb 17, 2026 — Referee Personality presets bundle temperature +
      // question_style. Default: "critic" — balanced tough-but-fair tone.
      // Picking a preset locks the two underlying knobs. Picking "custom"
      // restores the user's most-recently-tuned custom values (saved as
      // referee_custom_memory in localStorage; survives preset switches).
      personality: "critic",
      temperature: 0.4,
      question_style_preset: "punchy",
      question_style_custom: null,
      // Sticky memory of the user's last hand-tuned values, restored when
      // they switch back to personality="custom". Null until first edit.
      referee_custom_memory: null,
    };
  }
  return { ...base, temperature: 0.7 };
}

// Feb 17, 2026 — preset bundles. Each maps to a temperature + question_style.
// Picking a preset OVERRIDES both knobs and hides the advanced sliders. The
// "custom" pseudo-preset reveals them again and restores the user's last
// hand-tuned values.
const REFEREE_PERSONALITIES = [
  {
    id: "fact_checker",
    label: "Fact-Checker",
    blurb: "Tight, factual yes/no traps. Strict adherence to corpus sources.",
    temperature: 0.2,
    question_style_preset: "crisp",
  },
  {
    id: "critic",
    label: "Critic",
    blurb: "Tough but fair. Probes weak reasoning, demands clarity.",
    temperature: 0.4,
    question_style_preset: "punchy",
  },
  {
    id: "explorer",
    label: "Explorer",
    blurb: "Open-ended, curious. Surfaces unexpected angles.",
    temperature: 0.7,
    question_style_preset: "curious",
  },
  {
    id: "provocateur",
    label: "Provocateur",
    blurb: "Sharp, sometimes uncomfortable. Challenges deep assumptions.",
    temperature: 0.9,
    question_style_preset: "punchy",
  },
];

function loadStored(slotKey, variant) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + slotKey);
    if (!raw) return defaultsFor(variant);
    return { ...defaultsFor(variant), ...JSON.parse(raw) };
  } catch { return defaultsFor(variant); }
}

function saveStored(slotKey, value) {
  try { localStorage.setItem(STORAGE_PREFIX + slotKey, JSON.stringify(value)); } catch { /* */ }
}

export default function BattleTunePill({
  slotKey,         // unique localStorage key (e.g., "challenger", "opponent", "referee", "cross")
  variant = "contestant",  // "contestant" | "referee"
  label,           // header text inside the popover (e.g., "Challenger silo will use")
  onChange,        // (value) => void — called whenever any dial moves
  registry = BATTLE_REGISTRY_FALLBACK,
}) {
  const [value, setValue] = useState(() => loadStored(slotKey, variant));
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  // Push initial value up to parent on mount + every change
  useEffect(() => { onChange?.(value); /* eslint-disable-next-line */ }, []);
  useEffect(() => { saveStored(slotKey, value); onChange?.(value); /* eslint-disable-next-line */ }, [value]);

  // Outside-click close
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const set = (patch) => setValue((v) => ({ ...v, ...patch }));

  // Find current model's cap + meta
  const modelInfo = (() => {
    for (const p of registry.providers) {
      if (p.max_output_cap?.[value.model]) {
        return { cap: p.max_output_cap[value.model], provider: p.provider, label: p.label };
      }
    }
    return { cap: 8192, provider: value.provider, label: value.provider };
  })();

  // Clamp on model swap
  useEffect(() => {
    if (value.max_tokens > modelInfo.cap) set({ max_tokens: modelInfo.cap });
    // eslint-disable-next-line
  }, [modelInfo.cap]);

  const isReferee = variant === "referee";

  const setAsDefaultLength = () => set({ default_max_tokens: value.max_tokens });
  const restoreDefaultLength = () => {
    if (value.default_max_tokens) set({ max_tokens: value.default_max_tokens });
  };
  const resetModel = () => {
    if (!window.confirm("Reset this LLM back to default?")) return;
    const d = defaultsFor(variant);
    set({ provider: d.provider, model: d.model });
  };

  return (
    <div className="relative" ref={popRef} data-testid={`battle-tune-pill-${slotKey}`}>
      {/* The pill button — long, gray, label = current model name */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-[#E8E5DF] hover:bg-[#DDD9D2] border border-[#D8D6D2] text-[#1C1B1A] text-sm transition"
        data-testid={`battle-tune-pill-button-${slotKey}`}
      >
        <span className="flex items-center gap-2 truncate">
          <Cpu size={14} weight="duotone" className="text-[#4A5D4E] shrink-0" />
          <span className="font-mono text-xs truncate">{value.model}</span>
        </span>
        <CaretDown size={12} weight="duotone" className="text-[#6B6A68] shrink-0" />
      </button>

      {/* Popover */}
      {open && (
        <div
          className="absolute left-0 right-0 mt-2 z-30 bg-white border border-[#D8D6D2] rounded-lg shadow-2xl p-5 max-h-[70vh] overflow-y-auto"
          style={{ minWidth: 360 }}
          data-testid={`battle-tune-popover-${slotKey}`}
        >
          {/* Top bar */}
          <div className="flex items-start justify-between gap-3 mb-1">
            <h3 className="font-serif-display text-base flex items-center gap-2">
              <Cpu size={14} weight="duotone" className="text-[#4A5D4E]" />
              {label || "Tune this LLM"}
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={resetModel}
                title="Reset this LLM to default"
                className="text-[10px] px-2 py-1 rounded-md border border-[#D8D6D2] text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] flex items-center gap-1"
                data-testid={`battle-tune-reset-model-${slotKey}`}
              >
                <ArrowsCounterClockwise size={10} /> Reset LLM
              </button>
              <button type="button" onClick={() => setOpen(false)} className="text-[#6B6A68] hover:text-[#1C1B1A]"><X size={14} /></button>
            </div>
          </div>
          <p className="text-[11px] text-[#6B6A68] mb-4">
            Battle-scoped — these dials only affect this match. Last-used values are remembered for next time.
          </p>

          {/* Model dropdown */}
          <label className="block text-xs text-[#6B6A68] mb-1.5 flex items-center gap-1">
            Model
            <InfoTip icon="eye" iconSize={12} width={300} placement="bottom-right" testid={`battle-tune-info-model-${slotKey}`} label="Model help">
              Pick the LLM this side uses for the match. Each model has its own output cap and pricing — the Max output slider rescales when you change the model.
            </InfoTip>
          </label>
          <select
            value={value.model}
            onChange={(e) => {
              const newModel = e.target.value;
              for (const p of registry.providers) {
                if (p.models.includes(newModel)) {
                  set({ provider: p.provider, model: newModel });
                  return;
                }
              }
              set({ model: newModel });
            }}
            className="appearance-none w-full bg-white border border-[#D8D6D2] rounded-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            data-testid={`battle-tune-model-${slotKey}`}
          >
            {registry.providers.map((p) => (
              <optgroup key={p.provider} label={p.label}>
                {p.models.map((m) => (
                  <option key={m} value={m}>
                    {m}{m === p.recommended ? "  · recommended" : ""}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {value.model !== registry.platform_default_model && (
            <p className="text-[10px] text-[#4A5D4E] mt-1 flex items-center gap-1">
              <Sparkle size={9} weight="duotone" /> Overrides the platform default for this match only.
            </p>
          )}

          {/* Feb 14, 2026 — Answer style dropdown. Contestant-side only.
              Battle-scoped; persists per slot in localStorage like the rest of the pill.
              Feb 17, 2026 — Question Style moved to the Referee variant
              because the REFEREE is the LLM that authors round questions.
              Contestants only answer, so they don't get a question-style knob. */}
          {!isReferee && (
            <div className="mt-4 pt-4 border-t border-[#E5E2DD] space-y-3">
              <p className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">
                Answer style · battle-scoped
              </p>
              <StyleSelector
                variant="answer"
                preset={value.answer_style_preset || "concise"}
                custom={value.answer_style_custom || null}
                onChange={({ preset, custom }) => set({ answer_style_preset: preset, answer_style_custom: custom })}
                testidPrefix={`battle-tune-${slotKey}`}
                compact
              />
              <p className="text-[10px] text-[#6B6A68] leading-snug italic">
                Shapes how long this silo's answers run. Doesn't cap tokens — the LLM keeps its full thinking budget.
              </p>
            </div>
          )}

          {/* Referee-only Personality preset + advanced knobs ============
              Feb 17, 2026. Personality bundles temperature + question_style.
              Custom reveals the existing sliders and recalls the user's last
              hand-tuned values. */}
          {isReferee && (
            <>
              <div className="mt-4 pt-4 border-t border-[#E5E2DD] space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">
                    Referee personality
                  </p>
                  <InfoTip icon="eye" iconSize={12} width={300} placement="bottom-right"
                    testid={`battle-tune-info-personality-${slotKey}`}
                    label="Referee personality help">
                    Each personality bundles a temperature + a question style. Pick "Custom" to set them by hand — your last manual values are remembered.
                  </InfoTip>
                </div>
                <div className="space-y-1.5" data-testid={`battle-tune-personality-${slotKey}`}>
                  {REFEREE_PERSONALITIES.map((p) => (
                    <label key={p.id}
                      className={`flex items-start gap-2.5 px-2.5 py-2 rounded-md cursor-pointer transition-colors ${
                        (value.personality || "critic") === p.id ? "bg-[#F0F1EE] border border-[#4A5D4E]/40" : "border border-transparent hover:bg-[#F8F7F4]"
                      }`}>
                      <input
                        type="radio"
                        name={`referee-personality-${slotKey}`}
                        className="mt-0.5 accent-[#4A5D4E]"
                        checked={(value.personality || "critic") === p.id}
                        onChange={() => {
                          // Picking a preset: stash whatever custom values
                          // were live (so the user can return to Custom and
                          // recall them), then snap to the preset's values.
                          setValue((v) => {
                            const isLeavingCustom = (v.personality || "critic") === "custom";
                            const memory = isLeavingCustom
                              ? { temperature: v.temperature, question_style_preset: v.question_style_preset, question_style_custom: v.question_style_custom || null }
                              : v.referee_custom_memory;
                            return {
                              ...v,
                              personality: p.id,
                              temperature: p.temperature,
                              question_style_preset: p.question_style_preset,
                              question_style_custom: null,
                              referee_custom_memory: memory || v.referee_custom_memory,
                            };
                          });
                        }}
                        data-testid={`battle-tune-personality-${slotKey}-${p.id}`}
                      />
                      <div className="text-xs leading-tight">
                        <div className="text-[#1C1B1A] font-medium">{p.label}</div>
                        <div className="text-[10px] text-[#6B6A68] mt-0.5">{p.blurb}</div>
                      </div>
                    </label>
                  ))}
                  {/* Custom — reveals advanced sliders below */}
                  <label className={`flex items-start gap-2.5 px-2.5 py-2 rounded-md cursor-pointer transition-colors ${
                    (value.personality || "critic") === "custom" ? "bg-[#F0F1EE] border border-[#4A5D4E]/40" : "border border-transparent hover:bg-[#F8F7F4]"
                  }`}>
                    <input
                      type="radio"
                      name={`referee-personality-${slotKey}`}
                      className="mt-0.5 accent-[#4A5D4E]"
                      checked={(value.personality || "critic") === "custom"}
                      onChange={() => {
                        // Restore the user's last hand-tuned values if we
                        // have them, otherwise keep whatever's currently live.
                        setValue((v) => {
                          const mem = v.referee_custom_memory;
                          if (!mem) return { ...v, personality: "custom" };
                          return {
                            ...v,
                            personality: "custom",
                            temperature: mem.temperature ?? v.temperature,
                            question_style_preset: mem.question_style_preset ?? v.question_style_preset,
                            question_style_custom: mem.question_style_custom ?? null,
                          };
                        });
                      }}
                      data-testid={`battle-tune-personality-${slotKey}-custom`}
                    />
                    <div className="text-xs leading-tight">
                      <div className="text-[#1C1B1A] font-medium">Custom · show advanced</div>
                      <div className="text-[10px] text-[#6B6A68] mt-0.5">Tune temperature + question style by hand.</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Advanced sliders — only when personality === "custom" */}
              {(value.personality || "critic") === "custom" && (
                <>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <label className="text-xs text-[#6B6A68] flex items-center gap-1">
                      Temperature
                      <InfoTip icon="eye" iconSize={12} width={300} placement="bottom-right" testid={`battle-tune-info-temp-${slotKey}`} label="Referee temperature help">
                        A "Precise" referee will be a strict "fact-checker." While a "Wild" referee might find creative connections between two theologies or ideas.
                      </InfoTip>
                    </label>
                    <span className="tabular-nums text-[#1C1B1A] font-medium text-xs">{(value.temperature ?? 0.3).toFixed(2)}</span>
                  </div>
                  <input
                    type="range" min="0" max="1" step="0.05"
                    value={value.temperature ?? 0.3}
                    onChange={(e) => {
                      // Manual edit auto-pulls us into Custom mode and refreshes memory.
                      const t = parseFloat(e.target.value);
                      setValue((v) => ({
                        ...v,
                        personality: "custom",
                        temperature: t,
                        referee_custom_memory: { temperature: t, question_style_preset: v.question_style_preset, question_style_custom: v.question_style_custom || null },
                      }));
                    }}
                    className="w-full mt-1.5 accent-[#4A5D4E]"
                    data-testid={`battle-tune-temp-slider-${slotKey}`}
                  />
                  <div className="flex justify-between text-[9px] text-[#6B6A68] mt-1">
                    <span>Precise</span>
                    <span>Wild</span>
                  </div>

                  <div className="mt-4 pt-4 border-t border-[#E5E2DD] space-y-3">
                    <p className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">
                      Question style · battle-scoped
                    </p>
                    <StyleSelector
                      variant="question"
                      preset={value.question_style_preset || "punchy"}
                      custom={value.question_style_custom || null}
                      onChange={({ preset, custom }) => {
                        setValue((v) => ({
                          ...v,
                          personality: "custom",
                          question_style_preset: preset,
                          question_style_custom: custom,
                          referee_custom_memory: { temperature: v.temperature, question_style_preset: preset, question_style_custom: custom },
                        }));
                      }}
                      testidPrefix={`battle-tune-${slotKey}`}
                      compact
                    />
                    <p className="text-[10px] text-[#6B6A68] leading-snug italic">
                      Shapes how the referee phrases round questions to the contenders.
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          {/* Max output length slider */}
          <div className="mt-4 flex items-center justify-between gap-3">
            <label className="text-xs text-[#6B6A68] flex items-center gap-1">
              Max output length
              <InfoTip icon="eye" iconSize={12} width={320} placement="bottom-right" testid={`battle-tune-info-output-${slotKey}`} label="Output length help">
                How many tokens this side can produce in one answer. Current cap: <strong>{modelInfo.cap.toLocaleString()}</strong>. Note: Gemini 2.5 Pro reserves part of this budget for internal reasoning — keep it at <strong>4,096+</strong> or you'll get empty responses.
              </InfoTip>
            </label>
            <span className="tabular-nums text-[#1C1B1A] font-medium text-xs">
              {(value.max_tokens || 0).toLocaleString()}<span className="text-[#6B6A68] font-normal"> tokens</span>
            </span>
          </div>
          <input
            type="range" min="64" max={modelInfo.cap} step="64"
            value={Math.min(value.max_tokens || 4096, modelInfo.cap)}
            onChange={(e) => set({ max_tokens: parseInt(e.target.value, 10) })}
            className="w-full mt-1.5 accent-[#4A5D4E]"
            data-testid={`battle-tune-tokens-slider-${slotKey}`}
          />
          <div className="flex justify-between text-[9px] text-[#6B6A68] mt-1 tabular-nums">
            <span>64</span>
            <span>{Math.round(modelInfo.cap * 0.5).toLocaleString()}</span>
            <span>{modelInfo.cap.toLocaleString()}</span>
          </div>
          {/* May 12, 2026 — inline warning when the dial is below the
              floor Gemini 2.5 Pro's thinking budget needs. We auto-bump
              server-side regardless, but flag it here so users don't
              think their dial is the active setting. */}
          {(value.max_tokens || 0) < 4000 && /gemini-2\.5-pro/i.test(modelInfo.model_id || modelInfo.id || "") && (
            <div className="mt-2 px-2.5 py-1.5 rounded-md bg-[#FFF4E0] border border-[#A06800]/30 text-[10px] text-[#A06800] leading-snug">
              <strong>Heads up:</strong> Gemini 2.5 Pro needs ≥ 4,000 tokens for its thinking budget. We'll auto-bump this to 4,000 at battle time — you'll be billed for thinking tokens either way.
            </div>
          )}
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={setAsDefaultLength}
              className="text-[10px] px-2 py-1 rounded-md border border-[#D8D6D2] hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1"
              data-testid={`battle-tune-set-default-${slotKey}`}
            >
              <BookmarkSimple size={10} weight="duotone" /> Set as default
            </button>
            {value.default_max_tokens && (
              <button
                type="button"
                onClick={restoreDefaultLength}
                className="text-[10px] text-[#6B6A68] hover:text-[#C16E5A]"
              >
                ↺ {value.default_max_tokens.toLocaleString()}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
